import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SongCard from "@/components/SongCard";
import { ArrowRight, Play, BookOpen } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

const HERO_BG = "https://images.unsplash.com/photo-1514519273132-6a1abd48302c?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [songs, setSongs] = useState([]);
  const [popular, setPopular] = useState([]);
  const [verse, setVerse] = useState(null);
  const { play } = usePlayer();

  useEffect(() => {
    api.get("/songs/featured").then((r) => setFeatured(r.data));
    api.get("/songs", { params: { sort: "latest", limit: 24 } }).then((r) => setSongs(r.data));
    api.get("/songs", { params: { sort: "popular", limit: 6 } }).then((r) => setPopular(r.data));
    api.get("/daily-verse").then((r) => setVerse(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative mx-3 md:mx-6 mt-3 rounded-3xl overflow-hidden">
        <div
          className="relative min-h-[520px] md:min-h-[600px] w-full"
          style={{ backgroundImage: `linear-gradient(135deg, rgba(20,22,25,0.82), rgba(20,22,25,0.55)), url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="relative h-full flex flex-col items-center justify-center text-center px-6 py-24">
            <p className="eyebrow text-white/70 mb-6 fade-up">Christian Music Videos</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.02] text-white font-light max-w-5xl fade-up" style={{ animationDelay: "80ms" }}>
              Music that lifts hearts<br /><em className="not-italic">and sparks hope.</em>
            </h1>
            {verse && (
              <div data-testid="daily-verse" className="mt-8 max-w-xl fade-up" style={{ animationDelay: "140ms" }}>
                <p className="text-white/85 font-display italic text-lg md:text-xl leading-relaxed">"{verse.text}"</p>
                <p className="mt-2 text-[10px] tracking-[0.28em] uppercase text-white/60 inline-flex items-center gap-1.5">
                  <BookOpen size={11} strokeWidth={1.5} /> {verse.ref} · Verse of the day
                </p>
              </div>
            )}
            <p className="mt-8 text-white/80 text-base md:text-lg max-w-xl leading-relaxed fade-up" style={{ animationDelay: "220ms" }}>
              A distraction-free sanctuary for worship songs and music videos. Preview freely, own honestly — MP3 for $1.99, MP4 for $2.99.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3 fade-up" style={{ animationDelay: "240ms" }}>
              {featured && (
                <button
                  onClick={() => play(featured, "preview")}
                  data-testid="hero-preview-btn"
                  className="btn-pill bg-white text-neutral-900 hover:bg-neutral-100"
                >
                  <Play size={14} strokeWidth={1.5} /> Preview featured song
                </button>
              )}
              <Link to="/songs" data-testid="hero-browse-btn" className="btn-pill border border-white/30 text-white hover:bg-white/10">
                Browse all songs <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured banner */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20">
          <div className="rounded-3xl overflow-hidden relative group aspect-[21/9] md:aspect-[21/8]">
            <img src={featured.cover_url} alt={featured.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
            <div className="absolute inset-y-0 left-0 flex items-center px-8 md:px-16 max-w-2xl">
              <div className="text-white">
                <p className="eyebrow text-white/70 mb-3">Featured this week</p>
                <h2 className="font-display text-4xl md:text-6xl font-light">{featured.title}</h2>
                <p className="mt-4 text-white/80 max-w-lg line-clamp-3">{featured.description}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button onClick={() => play(featured, "preview")} className="btn-pill bg-white text-neutral-900">
                    <Play size={14} strokeWidth={1.5} /> 30s preview
                  </button>
                  <Link to={`/songs/${featured.id}`} className="btn-pill border border-white/40 text-white hover:bg-white/10">
                    Watch full song →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Latest releases grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Latest releases</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-light">Fresh from the studio.</h2>
          </div>
          <Link to="/songs" className="text-sm text-neutral-600 hover:text-neutral-900 hidden sm:inline-flex items-center gap-1">
            View all <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {songs.slice(0, 6).map((s, i) => <SongCard key={s.id} song={s} index={i} />)}
        </div>
      </section>

      {/* Popular songs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="mb-10">
          <p className="eyebrow mb-3">Most played</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-light">Popular right now.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {popular.map((s, i) => <SongCard key={s.id} song={s} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 mb-16">
        <div className="rounded-3xl bg-neutral-900 text-white px-8 md:px-16 py-16 md:py-20 relative overflow-hidden">
          <div className="max-w-2xl relative">
            <p className="eyebrow text-white/60 mb-4">The store</p>
            <h2 className="font-display text-4xl md:text-6xl font-light leading-tight">Every song, honestly priced.</h2>
            <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
              No subscriptions, no bundles. MP3 tracks at $1.99, MP4 videos at $2.99. Own what you love — instantly.
            </p>
            <Link to="/store" data-testid="cta-store-btn" className="btn-pill bg-white text-neutral-900 mt-8">
              Visit the store <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
